import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { FileText, Table2, Loader2, Search, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Eye, EyeOff, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import api, { baseURL } from "~/services/api";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";

const ITEMS_PER_PAGE = 8;

const LEVEL_COLORS = {
  Basic: "#1e40af",
  "Pre-Intermediate": "#f97316",
  Intermediate: "#000000",
  "Pre-Advanced": "#06b6d4",
  Advanced: "#a855f7",
  Proficient: "#22c55e",
  MBE: "#64748b",
  MBE2: "#f43f5e",
};

const TEACHER_COLORS = [
  "#1e40af",
  "#f97316",
  "#22c55e",
  "#06b6d4",
  "#a855f7",
  "#f43f5e",
  "#64748b",
  "#fb923c",
];

const GROUP_COLORS = [
  "#0ea5e9",
  "#16a34a",
  "#f59e0b",
  "#8b5cf6",
  "#ef4444",
  "#14b8a6",
  "#94a3b8",
  "#7c3aed",
];

const SHIFT_COLORS = {
  Morning: "#1e40af",
  "Morning/Afternoon": "#f97316",
  Afternoon: "#22c55e",
  Evening: "#06b6d4",
};

const ALL_SHIFTS = [
  "Morning",
  "Morning/Afternoon",
  "Afternoon",
  "Evening",
];

export default function FrequencyCharts() {
  const filial = useSelector((state) => state.auth?.filial);
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [selectedTeacher, setSelectedTeacher] = useState("all");
  const [selectedShift, setSelectedShift] = useState("all");
  const [selectedChart, setSelectedChart] = useState("overall");
  const [showModal, setShowModal] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [studentsData, setStudentsData] = useState([]);
  const [groups, setGroups] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [availableShifts, setAvailableShifts] = useState([]);
  
  const [visibleItems, setVisibleItems] = useState({});
  const [tempVisibleItems, setTempVisibleItems] = useState({});
  const [highlightedLine, setHighlightedLine] = useState(null);

  const [displayMode, setDisplayMode] = useState("top-bottom");
  const [topN, setTopN] = useState(3);
  const [bottomN, setBottomN] = useState(3);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedItemsIds, setSelectedItemsIds] = useState([]);
  const [itemsMetadata, setItemsMetadata] = useState({});
  const [allAvailableItems, setAllAvailableItems] = useState([]);
  const [loadingAvailableItems, setLoadingAvailableItems] = useState(false);

  const [lastFilterParams, setLastFilterParams] = useState(null);

  async function fetchFilters(params = {}) {
    const paramsKey = JSON.stringify(params);
    if (paramsKey === lastFilterParams) return;
    
    setLoadingFilters(true);
    try {
      const { data } = await api.get("/frequency-dashboard/filters", { params });
      setGroups(data.groups || []);
      setTeachers(data.teachers || []);
      setAvailableShifts(data.shifts || []);
      if (selectedShift !== 'all' && !(data.shifts || []).includes(selectedShift)) {
        setSelectedShift('all');
      }
      setLastFilterParams(paramsKey);
    } catch (error) {
      console.error("Error fetching filters:", error);
      setGroups([]);
      setTeachers([]);
    } finally {
      setLoadingFilters(false);
    }
  }

  useEffect(() => {
    if (periodFrom && periodTo) {
      setSelectedChart("overall");
      setLastFilterParams(null);
      setDisplayMode("all");
      const params = {};
      if (selectedShift !== "all") params.shift = selectedShift;
      fetchFilters(params);
      fetchFrequencyData("overall");
    }
  }, [periodFrom, periodTo, filial?.id]);

  useEffect(() => {
    if (periodFrom && periodTo) {
      setSelectedChart("overall");
      setDisplayMode("all");
      const params = {};
      
      if (selectedTeacher !== "all") {
        params.staff_id = selectedTeacher;
      }
      
      if (selectedGroup !== "all") {
        params.studentgroup_id = selectedGroup;
      }
      
      if (selectedShift !== "all") {
        params.shift = selectedShift;
      }
      
      fetchFilters(params);
      fetchFrequencyData("overall");
    }
  }, [selectedGroup, selectedTeacher, selectedShift]);

  async function fetchFrequencyData(modeOverride) {
    if (!periodFrom || !periodTo) return;

    setLoading(true);
    try {
      const params = {
        period_from: periodFrom,
        period_to: periodTo,
      };

      const modeToUse = modeOverride || selectedChart;
      if (modeToUse === "teacher") params.mode = "teacher";
      if (modeToUse === "shift") params.mode = "shift";
      if (modeToUse === "group") params.mode = "group";

    
      if (displayMode === "selected" && selectedItemsIds.length > 0) {
        if (modeToUse === "teacher") {
          params.staff_ids = selectedItemsIds.join(',');
        } else if (modeToUse === "group") {
          params.studentgroup_ids = selectedItemsIds.join(',');
        }
      } else {
      
        if (selectedGroup !== "all") params.studentgroup_id = selectedGroup;
        if (selectedTeacher !== "all") params.staff_id = selectedTeacher;
      }

      if (selectedShift !== "all") params.shift = selectedShift;

      
      if (displayMode === "top-bottom" && modeToUse !== "overall") {
        params.top = topN;
        params.bottom = bottomN;
      }

      const { data } = await api.get("/frequency-dashboard/overall-average-absences", { params });

      if (modeToUse === "teacher") {
       
        let teachersData = data.teachers || [];

        if (displayMode === "top-bottom" && data.teachers_top_bottom) {
          
          teachersData = [];
          (data.teachers_top_bottom || []).forEach(monthData => {
            if (monthData.top) teachersData.push(...monthData.top);
            if (monthData.bottom) teachersData.push(...monthData.bottom);
          });
         
          const uniqueKey = (item) => `${item.month}-${item.teacher}`;
          teachersData = Array.from(
            new Map(teachersData.map(item => [uniqueKey(item), item])).values()
          );
        }

        const teacherMap = {};
        const metadata = {};
        teachersData.forEach(item => {
          if (!teacherMap[item.month]) teacherMap[item.month] = { month: item.month };
          teacherMap[item.month][item.teacher] = item.presencePercent;
         
          if (item.staff_id && !metadata[item.teacher]) {
            metadata[item.teacher] = { id: item.staff_id, name: item.teacher };
          }
        });
        setChartData(Object.values(teacherMap));
        setItemsMetadata(metadata);

        const uniqueTeachers = [...new Set(teachersData.map(item => item.teacher))];
        const newVisible = {};
        const forcedShowAll = selectedTeacher !== 'all' || selectedGroup !== 'all' || selectedShift !== 'all';
        uniqueTeachers.forEach(teacher => {
          newVisible[teacher] = forcedShowAll ? true : (visibleItems[teacher] !== undefined ? visibleItems[teacher] : true);
        });
        setVisibleItems(newVisible);
      } else if (modeToUse === "shift") {
       
        let shiftsData = [];

        if (displayMode === "top-bottom" && data.shifts_top_bottom) {
         
          (data.shifts_top_bottom || []).forEach(monthData => {
            if (monthData.top) shiftsData.push(...monthData.top);
            if (monthData.bottom) shiftsData.push(...monthData.bottom);
          });
         
          const uniqueKey = (item) => `${item.month}-${item.shift}`;
          shiftsData = Array.from(
            new Map(shiftsData.map(item => [uniqueKey(item), item])).values()
          );
        } else {
          shiftsData = Array.isArray(data.overall) ? data.overall : [];
        }

        const monthMap = {};
        shiftsData.forEach((item) => {
          if (!monthMap[item.month]) monthMap[item.month] = { month: item.month };
          monthMap[item.month][item.shift] = item.presencePercent;
        });
        setChartData(Object.values(monthMap));

        let desiredShifts;
        if (displayMode === "top-bottom" && data.shifts_top_bottom) {
          
          desiredShifts = [...new Set(shiftsData.map(item => item.shift))];
        } else {
          desiredShifts = selectedShift !== "all" ? [selectedShift] : (availableShifts.length ? availableShifts : ALL_SHIFTS);
        }

        const newVisible = {};
        desiredShifts.forEach((shift) => {
          newVisible[shift] = visibleItems[shift] !== undefined ? visibleItems[shift] : true;
        });
        setVisibleItems(newVisible);
      } else if (modeToUse === "group") {
       
        let groupsData = data.groups || [];

        if (displayMode === "top-bottom" && data.groups_top_bottom) {
       
          groupsData = [];
          (data.groups_top_bottom || []).forEach(monthData => {
            if (monthData.top) groupsData.push(...monthData.top);
            if (monthData.bottom) groupsData.push(...monthData.bottom);
          });
         
          const uniqueKey = (item) => `${item.month}-${item.group}`;
          groupsData = Array.from(
            new Map(groupsData.map(item => [uniqueKey(item), item])).values()
          );
        }

        const groupMap = {};
        const metadata = {};
        groupsData.forEach(item => {
          if (!groupMap[item.month]) groupMap[item.month] = { month: item.month };
          groupMap[item.month][item.group] = item.presencePercent;
        
          if (item.group_id && !metadata[item.group]) {
            metadata[item.group] = { id: item.group_id, name: item.group };
          }
        });
        setChartData(Object.values(groupMap));
        setItemsMetadata(metadata);

        const uniqueGroups = [...new Set(groupsData.map(item => item.group))];
        const newVisible = {};
        const forcedShowAll = selectedTeacher !== 'all' || selectedGroup !== 'all' || selectedShift !== 'all';
        uniqueGroups.forEach(group => {
          newVisible[group] = forcedShowAll ? true : (visibleItems[group] !== undefined ? visibleItems[group] : true);
        });
        setVisibleItems(newVisible);
      } else {
        setChartData(data.overall || []);
      }

      setStudentsData(data.students || []);
    } catch (error) {
      console.error("Error fetching frequency data:", error);
      toast(error?.response?.data?.error || "Erro ao buscar dados", { type: "error", autoClose: 3000 });
      setChartData([]);
      setStudentsData([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchFrequencyData();
  }, [periodFrom, periodTo, selectedGroup, selectedTeacher, selectedShift, selectedChart, filial?.id, displayMode, topN, bottomN, selectedItemsIds]);

  async function handleExportExcel() {
    if (!periodFrom || !periodTo) {
      toast("Por favor, selecione o período antes de exportar", { type: "warning", autoClose: 2500 });
      return;
    }

    setExportingExcel(true);
    try {
      const params = {
        period_from: periodFrom,
        period_to: periodTo,
      };

      
      if (displayMode === "selected" && selectedItemsIds.length > 0) {
        if (selectedChart === "teacher") {
          params.staff_ids = selectedItemsIds.join(',');
        } else if (selectedChart === "group") {
          params.studentgroup_ids = selectedItemsIds.join(',');
        }
      } else {
       
        if (selectedGroup !== "all") params.studentgroup_id = selectedGroup;
        if (selectedTeacher !== "all") params.staff_id = selectedTeacher;
      }

      if (selectedShift !== "all") params.shift = selectedShift;

      const { data } = await api.get("/frequency-dashboard/overall-average-absences/excel", { params });
      const name = data?.name || `overall_average_absences_${periodFrom}_to_${periodTo}.xlsx`;
      saveAs(`${baseURL}/get-file/${name}`, name);
      toast("Excel gerado com sucesso!", { type: "success", autoClose: 2500 });
    } catch (err) {
      console.error("Error exporting excel:", err);
      toast(err?.response?.data?.error || "Falha ao gerar Excel", { type: "error", autoClose: 3000 });
    } finally {
      setExportingExcel(false);
    }
  }

  async function handleExportPDF() {
    if (!periodFrom || !periodTo) {
      toast("Por favor, selecione o período antes de exportar", { type: "warning", autoClose: 2500 });
      return;
    }

    setExportingPDF(true);
    try {
      const params = {
        period_from: periodFrom,
        period_to: periodTo,
      };

    
      if (displayMode === "selected" && selectedItemsIds.length > 0) {
        if (selectedChart === "teacher") {
          params.staff_ids = selectedItemsIds.join(',');
        } else if (selectedChart === "group") {
          params.studentgroup_ids = selectedItemsIds.join(',');
        }
      } else {
        
        if (selectedGroup !== "all") params.studentgroup_id = selectedGroup;
        if (selectedTeacher !== "all") params.staff_id = selectedTeacher;
      }

      if (selectedShift !== "all") params.shift = selectedShift;

      const { data } = await api.get("/frequency-dashboard/overall-average-absences/pdf", {
        params,
        responseType: "blob"
      });

      const pdfBlob = new Blob([data], { type: "application/pdf" });
      const name = `frequency_dashboard_${periodFrom}_to_${periodTo}.pdf`;
      saveAs(pdfBlob, name);
      toast("PDF gerado com sucesso!", { type: "success", autoClose: 2500 });
    } catch (err) {
      console.error("Error exporting PDF:", err);
      toast(err?.response?.data?.error || "Falha ao gerar PDF", { type: "error", autoClose: 3000 });
    } finally {
      setExportingPDF(false);
    }
  }

  const fetchAllAvailableItems = async () => {
    if (!periodFrom || !periodTo) return;

    setLoadingAvailableItems(true);
    try {
      const { data } = await api.get("/frequency-dashboard/available-filters");

      
      let items = [];
      if (selectedChart === "teacher") {
        items = (data.teachers || []).map(t => ({
          id: t.id,
          name: t.name
        }));
      } else if (selectedChart === "group") {
        items = (data.groups || []).map(g => ({
          id: g.id,
          name: g.name
        }));
      } else if (selectedChart === "shift") {
        items = (data.shifts || []).map(s => ({
          id: s, 
          name: s
        }));
      }

      setAllAvailableItems(items);
    } catch (error) {
      console.error("Error fetching available items:", error);
      toast(error?.response?.data?.error || "Erro ao buscar itens disponíveis", { type: "error", autoClose: 3000 });
      setAllAvailableItems([]);
    } finally {
      setLoadingAvailableItems(false);
    }
  };

  const handleOpenModal = async () => {
    if (displayMode === "selected") {
     
      setTempVisibleItems({});
      setShowModal(true);
      await fetchAllAvailableItems();
    } else {
      setTempVisibleItems({ ...visibleItems });
      setShowModal(true);
    }
  };

  const handleSaveModal = () => {
    if (displayMode === "selected") {
     
      const ids = selectedItems
        .map(itemName => {
          const item = allAvailableItems.find(i => i.name === itemName);
          return item?.id;
        })
        .filter(Boolean);
      setSelectedItemsIds(ids);
    } else {
      setVisibleItems({ ...tempVisibleItems });
      setDisplayMode("all");
    }
    setShowModal(false);
    setAllAvailableItems([]);
  };

  const handleCancelModal = () => {
    setShowModal(false);
    setAllAvailableItems([]);
  };

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredStudents = studentsData.filter(student => {
    const matchesSearch = student.student_full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.registration_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.filial?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         student.group?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const PERFORMANCE_ORDER = { 'Normal': 1, 'Critical': 2 };
  const LEVEL_ORDER = {
    'Basic': 1,
    'Pre-Intermediate': 2,
    'Intermediate': 3,
    'Pre-Advanced': 4,
    'Advanced': 5,
    'Proficient': 6,
    'MBE': 7,
    'MBE2': 8
  };

  const sortedStudents = [...filteredStudents].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (sortConfig.key === 'presence') {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    } else if (sortConfig.key === 'performance') {
      aValue = PERFORMANCE_ORDER[aValue] || 999;
      bValue = PERFORMANCE_ORDER[bValue] || 999;
    } else if (sortConfig.key === 'level') {
      aValue = LEVEL_ORDER[aValue] || 999;
      bValue = LEVEL_ORDER[bValue] || 999;
    } else {
      aValue = aValue?.toString().toLowerCase() || '';
      bValue = bValue?.toString().toLowerCase() || '';
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const totalPages = Math.ceil(sortedStudents.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentStudents = sortedStudents.slice(startIndex, endIndex);

  const getPerformanceColor = (performance) => {
    switch (performance) {
      case "Normal":
        return "text-green-800";
      case "Critical":
        return "text-red-800";
      default:
        return "text-gray-800";
    }
  };

  const getPerformanceBackground = (performance) => {
    switch (performance) {
      case "Normal":
        return "#BDFFD9";
      case "Critical":
        return "#FFCCCC";
      default:
        return "#e5e7eb";
    }
  };

  const getMonthName = (monthStr) => {
    if (!monthStr) return "";
    const [year, month] = monthStr.split("-");
    const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return months[parseInt(month) - 1] || monthStr;
  };

  const chartDataWithNames = chartData.map(item => ({
    ...item,
    monthName: getMonthName(item.month)
  }));

  const getColorForItem = (item, index) => {
    if (selectedChart === "teacher") {
      return TEACHER_COLORS[index % TEACHER_COLORS.length];
    } else if (selectedChart === "shift") {
      return SHIFT_COLORS[item] || "#000000";
    } else if (selectedChart === "group") {
      return GROUP_COLORS[index % GROUP_COLORS.length];
    } else if (selectedChart === "level") {
      return LEVEL_COLORS[item] || "#000000";
    }
    return "#0891b2";
  };

  const calculateAveragePerformance = (itemName) => {
    const values = chartData
      .map(d => d[itemName])
      .filter(v => v !== undefined && v !== null);
    if (values.length === 0) return 0;
    return values.reduce((sum, val) => sum + val, 0) / values.length;
  };

  const getRankedItems = () => {
 
    if (displayMode === "selected" && allAvailableItems.length > 0) {
      return allAvailableItems.map(item => ({
        name: item.name,
        id: item.id,
        average: calculateAveragePerformance(item.name),
        trend: calculateTrend(item.name),
        visible: selectedItems.includes(item.name)
      }))
      .sort((a, b) => b.average - a.average);
    }

   
    const items = Object.keys(visibleItems);
    return items
      .map(item => ({
        name: item,
        average: calculateAveragePerformance(item),
        trend: calculateTrend(item),
        visible: visibleItems[item]
      }))
      .sort((a, b) => b.average - a.average);
  };

  const calculateTrend = (itemName) => {
    const recentValues = chartData.slice(-3).map(d => d[itemName]).filter(v => v !== undefined);
    if (recentValues.length < 2) return 0;
    return recentValues[recentValues.length - 1] - recentValues[0];
  };

  const getItemsToDisplay = () => {
    const ranked = getRankedItems();
    
    if (displayMode === "top-bottom") {
      const topItems = ranked.slice(0, topN).map(i => i.name);
      const bottomItems = ranked.slice(-bottomN).map(i => i.name);
      return [...new Set([...topItems, ...bottomItems])];
    } else if (displayMode === "selected") {
      
      if (selectedChart === "teacher" || selectedChart === "group") {
        const idToName = Object.entries(itemsMetadata).reduce((acc, [name, meta]) => {
          if (meta.id) acc[meta.id] = name;
          return acc;
        }, {});

        const namesFromIds = selectedItemsIds
          .map(id => idToName[id])
          .filter(Boolean);

        if (namesFromIds.length) {
          return namesFromIds;
        }
      }

     
      return selectedItems;
    } else if (displayMode === "all") {
      return ranked.filter(i => visibleItems[i.name]).map(i => i.name);
    }
    return [];
  };

  const itemsToDisplay = getItemsToDisplay();

  const toggleItemSelection = (item) => {
    setSelectedItems(prev => 
      prev.includes(item) 
        ? prev.filter(i => i !== item)
        : [...prev, item]
    );
  };

  return (
    <div className="w-full h-full flex flex-col bg-white border overflow-hidden rounded-r-xl">
      <div className="bg-gray-100 px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText size={20} className="text-gray-700" />
          <h2 className="text-lg font-bold text-gray-700">Frequency</h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportExcel}
            disabled={exportingExcel || !periodFrom || !periodTo}
            className="text-md font-bold bg-secondary border text-zinc-500 hover:border-primary hover:text-primary rounded-md px-3 py-1.5 flex flex-row items-center justify-center text-xs gap-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {exportingExcel ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Table2 size={16} />
            )}
            Generate Worksheet
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exportingPDF || !periodFrom || !periodTo}
            className="text-md font-bold bg-secondary border text-zinc-500 hover:border-primary hover:text-primary rounded-md px-3 py-1.5 flex flex-row items-center justify-center text-xs gap-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {exportingPDF ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <FileText size={16} />
            )}
            PDF
          </button>
        </div>
      </div>

      <div className="px-4 py-3 border-b bg-gray-50 flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Period from / to</label>
          <div className="flex gap-2">
            <div className="flex gap-1">
              <select
                value={periodFrom.split("-")[1] || ""}
                onChange={(e) => {
                  const year = periodFrom.split("-")[0] || new Date().getFullYear();
                  setPeriodFrom(`${year}-${e.target.value}`);
                }}
                className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
              >
                <option value="">Month</option>
                <option value="01">January</option>
                <option value="02">February</option>
                <option value="03">March</option>
                <option value="04">April</option>
                <option value="05">May</option>
                <option value="06">June</option>
                <option value="07">July</option>
                <option value="08">August</option>
                <option value="09">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
              <select
                value={periodFrom.split("-")[0] || ""}
                onChange={(e) => {
                  const month = periodFrom.split("-")[1] || "01";
                  setPeriodFrom(`${e.target.value}-${month}`);
                }}
                className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
              >
                <option value="">Year</option>
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-1">
              <select
                value={periodTo.split("-")[1] || ""}
                onChange={(e) => {
                  const year = periodTo.split("-")[0] || new Date().getFullYear();
                  setPeriodTo(`${year}-${e.target.value}`);
                }}
                className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
              >
                <option value="">Month</option>
                <option value="01">January</option>
                <option value="02">February</option>
                <option value="03">March</option>
                <option value="04">April</option>
                <option value="05">May</option>
                <option value="06">June</option>
                <option value="07">July</option>
                <option value="08">August</option>
                <option value="09">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
              <select
                value={periodTo.split("-")[0] || ""}
                onChange={(e) => {
                  const month = periodTo.split("-")[1] || "01";
                  setPeriodTo(`${e.target.value}-${month}`);
                }}
                className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
              >
                <option value="">Year</option>
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Group</label>
          <select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            disabled={loadingFilters}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-48 disabled:opacity-50"
          >
            <option value="all">All Groups</option>
            {groups.map(group => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Teacher</label>
          <select
            value={selectedTeacher}
            onChange={(e) => setSelectedTeacher(e.target.value)}
            disabled={loadingFilters}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-48 disabled:opacity-50"
          >
            <option value="all">All Teachers</option>
            {teachers.map(teacher => (
              <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-600">Shift</label>
          <select
            value={selectedShift}
            onChange={(e) => setSelectedShift(e.target.value)}
            className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange w-48"
          >
            <option value="all">All Shifts</option>
            {availableShifts.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="flex-1" />
        <div className="text-xs text-gray-500">
          <p>Filters update automatically</p>
        </div>
      </div>

      <div className="p-4 overflow-y-auto flex-1">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-gray-400" size={48} />
          </div>
        ) : (
          <>
            <div className="bg-white border rounded-lg shadow-sm mb-6">
              <div className="px-6 pt-6 pb-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-end gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-semibold text-gray-600">Report</label>
                      <select
                        value={selectedChart}
                        onChange={(e) => {
                          setSelectedChart(e.target.value);
                          const anyFilter = selectedTeacher !== 'all' || selectedGroup !== 'all' || selectedShift !== 'all';
                          setDisplayMode(anyFilter ? 'all' : 'top-bottom');
                          setSelectedItems([]);
                          setSelectedItemsIds([]);
                        }}
                        className="px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                      >
                        <option value="overall">Overall Frequency</option>
                        <option value="teacher">Frequency by Teacher</option>
                        <option value="group">Frequency by Group</option>
                        <option value="shift">Frequency by Shift</option>
                      </select>
                    </div>
                    
                    {selectedChart !== "overall" && (
                      <>
                        <div className="flex flex-col gap-1">
                          <label className="text-xs font-semibold text-gray-600">Display</label>
                          <select
                            value={displayMode}
                            onChange={(e) => {
                              const newMode = e.target.value;
                              setDisplayMode(newMode);
                              if (newMode !== "selected") {
                                setSelectedItems([]);
                                setSelectedItemsIds([]);
                                setAllAvailableItems([]);
                              }
                            }}
                            className="px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-48"
                          >
                            <option value="top-bottom">Top & Bottom</option>
                            <option value="selected">Selected Items</option>
                            <option value="all">Show All</option>
                          </select>
                        </div>

                        {displayMode === "top-bottom" && (
                          <>
                            <div className="flex flex-col gap-1">
                              <label className="text-xs font-semibold text-gray-600">Top N</label>
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={topN}
                                onChange={(e) => setTopN(parseInt(e.target.value) || 3)}
                                className="px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-20"
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <label className="text-xs font-semibold text-gray-600">Bottom N</label>
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={bottomN}
                                onChange={(e) => setBottomN(parseInt(e.target.value) || 3)}
                                className="px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-20"
                              />
                            </div>
                          </>
                        )}

                        <button
                          onClick={handleOpenModal}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-semibold text-sm mt-5"
                        >
                          Details
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {selectedChart === "overall" ? (
                  <>
                    <h3 className="text-center font-bold text-gray-800 mb-4 text-lg">Overall Frequency</h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={chartDataWithNames}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="monthName" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={80} />
                        <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                        <Tooltip formatter={(value) => `${value}%`} />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Line 
                          type="monotone" 
                          dataKey="presencePercent" 
                          stroke="#0891b2" 
                          strokeWidth={3} 
                          dot={{ r: 5, fill: "#0891b2" }} 
                          name="OVERALL"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </>
                ) : (
                  <>
                    <h3 className="text-center font-bold text-gray-800 mb-4 text-lg">
                      {selectedChart === "teacher" ? "Frequency by Teacher" :
                       selectedChart === "shift" ? "Frequency by Shift" : "Frequency by Group"}
                    </h3>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart data={chartDataWithNames}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="monthName" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={80} />
                        <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />

                        {itemsToDisplay.map((item, index) => (
                          <Line
                            key={item}
                            type="monotone"
                            dataKey={item}
                            stroke={getColorForItem(item, index)}
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </>
                )}
              </div>


            </div>

            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b flex items-center gap-3">
                <div className="relative flex-1 max-w-xs">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-2 text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b">
                    <tr>
                      <th
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                        onClick={() => handleSort('student_full_name')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Student</span>
                          {sortConfig.key === 'student_full_name' ? (
                            sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                          ) : (
                            <ArrowUpDown size={14} className="opacity-40" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                        onClick={() => handleSort('registration_number')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Registration Number</span>
                          {sortConfig.key === 'registration_number' ? (
                            sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                          ) : (
                            <ArrowUpDown size={14} className="opacity-40" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                        onClick={() => handleSort('filial')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Filial</span>
                          {sortConfig.key === 'filial' ? (
                            sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                          ) : (
                            <ArrowUpDown size={14} className="opacity-40" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                        onClick={() => handleSort('shift')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Shift</span>
                          {sortConfig.key === 'shift' ? (
                            sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                          ) : (
                            <ArrowUpDown size={14} className="opacity-40" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                        onClick={() => handleSort('group')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Group</span>
                          {sortConfig.key === 'group' ? (
                            sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                          ) : (
                            <ArrowUpDown size={14} className="opacity-40" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                        onClick={() => handleSort('level')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Level</span>
                          {sortConfig.key === 'level' ? (
                            sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                          ) : (
                            <ArrowUpDown size={14} className="opacity-40" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                        onClick={() => handleSort('presence')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Presence</span>
                          {sortConfig.key === 'presence' ? (
                            sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                          ) : (
                            <ArrowUpDown size={14} className="opacity-40" />
                          )}
                        </div>
                      </th>
                      <th
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors select-none"
                        onClick={() => handleSort('performance')}
                      >
                        <div className="flex items-center gap-2">
                          <span>Performance</span>
                          {sortConfig.key === 'performance' ? (
                            sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                          ) : (
                            <ArrowUpDown size={14} className="opacity-40" />
                          )}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentStudents.length > 0 ? (
                      currentStudents.map((student, index) => (
                        <tr key={index} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors`}>
                          <td className="px-4 py-3 text-sm text-gray-800">{student.student_full_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-800">{student.registration_number}</td>
                          <td className="px-4 py-3 text-sm text-gray-800">{student.filial}</td>
                          <td className="px-4 py-3 text-sm text-gray-800">{student.shift}</td>
                          <td className="px-4 py-3 text-sm text-gray-800">{student.group}</td>
                          <td className="px-4 py-3 text-sm text-gray-800">{student.level}</td>
                          <td className="px-4 py-3 text-sm text-gray-800">{student.presence}%</td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getPerformanceColor(student.performance)}`}
                              style={{ backgroundColor: getPerformanceBackground(student.performance) }}
                            >
                              {student.performance}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-sm text-gray-500">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {currentStudents.length > 0 && (
                <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {startIndex + 1} to {Math.min(endIndex, sortedStudents.length)} of {sortedStudents.length} rows
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="p-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <span className="text-sm text-gray-600">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="p-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-800">
                {displayMode === "top-bottom" ? "View Details" : "Manage Items"} - {selectedChart === "teacher" ? "Teachers" : selectedChart === "shift" ? "Shifts" : "Groups"}
              </h3>
              {displayMode === "selected" && (
                <p className="text-sm text-gray-600 mt-2">
                  Select multiple items to display on the chart. Click "Apply Changes" to update the chart with your selection.
                </p>
              )}
              {displayMode === "top-bottom" && (
                <p className="text-sm text-gray-600 mt-2">
                  Viewing top {topN} and bottom {bottomN} items by average performance.
                </p>
              )}
            </div>

            <div className="flex-1 overflow-hidden flex">
              <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search items..."
                    className="w-full px-4 py-2 border rounded-md text-sm"
                  />
                </div>

                {loadingAvailableItems ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="animate-spin text-gray-400" size={32} />
                  </div>
                ) : (
                <table className="w-full">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      {displayMode !== "top-bottom" && (
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 w-12">
                          {displayMode === "selected" ? (
                            <input
                              type="checkbox"
                              checked={getRankedItems().every(item => selectedItems.includes(item.name))}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedItems(getRankedItems().map(item => item.name));
                                } else {
                                  setSelectedItems([]);
                                }
                              }}
                              className="cursor-pointer"
                            />
                          ) : (
                            <input
                              type="checkbox"
                              checked={Object.values(tempVisibleItems).every(v => v)}
                              onChange={(e) => {
                                const newState = {};
                                Object.keys(tempVisibleItems).forEach(key => {
                                  newState[key] = e.target.checked;
                                });
                                setTempVisibleItems(newState);
                              }}
                              className="cursor-pointer"
                            />
                          )}
                        </th>
                      )}
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Name</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Avg</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getRankedItems().map((item, idx) => {
                      const avg = item.average;
                      const trend = item.trend;
                      const isSelected = displayMode === "selected" ? selectedItems.includes(item.name) : tempVisibleItems[item.name];

                      return (
                        <tr
                          key={item.name}
                          className={`border-b hover:bg-gray-50 transition-colors ${
                            displayMode === "top-bottom" && (
                              idx < topN ? 'bg-green-50' :
                              idx >= getRankedItems().length - bottomN ? 'bg-red-50' : ''
                            )
                          }`}
                        >
                          {displayMode !== "top-bottom" && (
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  if (displayMode === "selected") {
                                    toggleItemSelection(item.name);
                                  } else {
                                    setTempVisibleItems({
                                      ...tempVisibleItems,
                                      [item.name]: e.target.checked,
                                    });
                                  }
                                }}
                                className="cursor-pointer"
                              />
                            </td>
                          )}
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: getColorForItem(item.name, idx) }}
                              />
                              <span className="text-sm text-gray-800 font-medium">{item.name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            {avg > 0 ? (
                              <span className={`text-sm font-semibold ${
                                avg >= 90 ? 'text-green-700' :
                                avg >= 75 ? 'text-yellow-700' :
                                'text-red-700'
                              }`}>
                                {avg.toFixed(1)}%
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">N/A</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              {avg > 0 ? (
                                <>
                                  {trend > 0 ? (
                                    <TrendingUp size={14} className="text-green-600" />
                                  ) : trend < 0 ? (
                                    <TrendingDown size={14} className="text-red-600" />
                                  ) : (
                                    <div className="w-3.5 h-3.5" />
                                  )}
                                  <span className={`text-xs ${
                                    trend > 0 ? 'text-green-700' :
                                    trend < 0 ? 'text-red-700' :
                                    'text-gray-500'
                                  }`}>
                                    {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                                  </span>
                                </>
                              ) : (
                                <span className="text-xs text-gray-400">N/A</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-between items-center gap-2">
              {displayMode === "selected" && (
                <div className="text-sm text-gray-600">
                  <span className="font-semibold">{selectedItems.length}</span> item{selectedItems.length !== 1 ? 's' : ''} selected
                </div>
              )}
              <div className="flex gap-2 ml-auto">
                <button
                  onClick={handleCancelModal}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-semibold text-sm"
                >
                  {displayMode === "top-bottom" ? "Close" : "Cancel"}
                </button>
                {displayMode !== "top-bottom" && (
                  <button
                    onClick={handleSaveModal}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-semibold text-sm"
                  >
                    Apply Changes
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
